<template>
  <article
    :class="[
      'guides-card-content-wrapper',
      isRuleActive ? 'active' : '',
      comingSoon ? '' : 'offset-action',
    ]"
    :aria-labelledby="`guide-${guideItem.id}`"
  >
    <h2 :id="`guide-${guideItem.id}`">
      <a
        v-if="guideItem.read_more_url"
        class="guide-card-rule-name guide-card-link"
        :href="guideItem.read_more_url"
        target="_blank"
        rel="noopener noreferrer"
      >
        <DpplSafeHtml :html="guideItem.name" />
        <!--The below for keeping eslint quiet-->
        <span class="d-none">Read more here</span>
      </a>
      <span v-else class="guide-card-rule-name">
        <DpplSafeHtml :html="guideItem.name" />
      </span>
      <span
        v-if="guideItem.new && !comingSoon"
        class="guide-card-rule-new"
      >
        New
      </span>
    </h2>
    <slot name="guide-card-content">
      <div
        v-if="guideItem.description?.length"
        class="guide-card-description"
      >
        <DpplSafeHtml :html="guideItem.description" />
      </div>
    </slot>
    <v-btn
      v-show="!comingSoon"
      class="guides-card-action"
      rounded="xl"
      variant="flat"
      @click="toggleRule"
    >
      <template #prepend>
        <span class="guide-card-action-icon-wrapper">
          <CheckMarkIcon class="guide-card-action-icon" />
        </span>
      </template>
      {{ actionLabel }}
    </v-btn>
  </article>
</template>

<script setup>
import { ref, computed } from 'vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import CheckMarkIcon from '@/assets/icon_checkmark_small.svg?component';

const props = defineProps({
  guideItem: {
    type: Object,
    default: () => {},
  },
  comingSoon: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(['submit']);

const isRuleActive = ref((props.guideItem?.active && !props.comingSoon) || false);

// Computed
const actionLabel = computed(() => (isRuleActive.value ? 'Enabled' : 'Disabled'));

const action = computed(() => (isRuleActive.value ? 'enable' : 'disable'));

// Methods
const toggleRule = () => {
  isRuleActive.value = !isRuleActive.value;
  emits('submit', {
    action: action.value,
    id: props.guideItem?.id,
  });
};
</script>

<style scoped lang="less">
.guides-card-content-wrapper {
  position: relative;
  background: @white;
  padding: 24px;
  border-radius: 10px;
  border: 1px solid @stroke-default;

  h2 {
    display: flex;
    align-items: center;
    line-height: normal;
    padding-bottom: 8px;

    .guide-card-rule-name {
      font-size: 16px;
      font-weight: 700;
    }

    .guide-card-rule-new {
      display: inline-flex;
      width: 40px;
      height: 20px;
      justify-content: center;
      align-items: center;
      margin-left: 8px;
      font-size: 12px;
      font-weight: 400;
      color: @white;
      background: @bg-brand-default;
      border-radius: 16px;
    }
  }

  &.offset-action {
    margin-bottom: 18px;
    padding-bottom: 42px;
  }

  .guide-card-description {
    font-size: 14px;
    font-weight: 300;
  }

  .guide-card-link {
    font-size: 14px;
    font-weight: 500;
    color: @text-brand;
    text-decoration: underline;

    &:visited {
      color: @text-brand;
    }
  }

  .guides-card-action {
    position: absolute;
    bottom: -18px;
    left: 24px;
    text-transform: capitalize;
    font-size: 16px;
    font-weight: 400;
    background: @grey-500;

    :deep(.v-btn__overlay) {
      display: none;
    }

    &:hover {
      background: @grey-600;

      .guide-card-action-icon :deep(path) {
        fill: @grey-600;
      }
    }

    .guide-card-action-icon-wrapper {
      width: 12px;
      height: 12px;
      display: flex;
      justify-content: center;
      align-items: center;
      background: @white;
      border-radius: 50%;
    }

    .guide-card-action-icon :deep(path) {
      fill: @grey-500;
    }
  }

  &.active {
    border-color: @text-brand;

    .guides-card-action {
      background: @bg-brand-default;
      color: @white;

      .guide-card-action-icon :deep(path) {
        fill: @icon-brand;
      }

      &:hover {
        background: @bg-brand-dark;
      }
    }
  }
}
</style>
